import server from 'common/utils/server';
import { reduce, values, flatten } from 'lodash';
import { dayOnlyDateFormat, DateTime } from 'common/utils/date';

const NULL_TREND_METRIC = {
  metric_name: '',
  question_identifier: '',
  translations: { en: { title: '' } },
};

export const companyTrendsState = (
  state = {
    availableFilters: {},
    defaultFilters: undefined,
    filterableQuestionsByIdentifier: {},
    filterableMetrics: [],
    selectedTab: {
      question_identifier: '',
      metric_name: '',
    },
    selectedQuestionAndMetric: {
      question_identifier: '',
      metric_name: '',
    },
    brandsByName: {},
  },
  action
) => {
  switch (action.type) {
    case 'INITIALIZATION_DATA': {
      const { trendMetricConfigs, defaultTrendsFilters } = action.data;
      const firstTrendMetric = trendMetricConfigs[0] || NULL_TREND_METRIC;
      const lastTrendMetric =
        trendMetricConfigs.slice(-1)[0] || NULL_TREND_METRIC;
      return {
        ...state,
        selectedTab: firstTrendMetric,
        selectedQuestionAndMetric: lastTrendMetric,
        defaultFilters: {
          start_date: DateTime.now()
            .minus({ days: 30 })
            .toFormat(dayOnlyDateFormat),
          end_date: DateTime.now().toFormat(dayOnlyDateFormat),
          ...defaultTrendsFilters,
        },
      };
    }

    case 'INITIALIZE/AVAILABLE_FILTERS': {
      const { availableFilters } = action;
      const brands = { data: {} };
      const brandsByName = {};
      if (availableFilters.brands) {
        Object.entries(availableFilters.brands.data).forEach(([id, name]) => {
          brands.data[name] = name;
          brandsByName[`${name}`] = id;
        });
      }
      return {
        ...state,
        availableFilters: {
          ...state.availableFilters,
          ...availableFilters,
          ...(brands ? { brands } : {}),
        },
        brandsByName,
      };
    }

    case 'INITIALIZE/AVAILABLE_QUESTIONS': {
      const { filterableQuestionsByIdentifier, filterableMetrics } = action;
      return {
        ...state,
        filterableQuestionsByIdentifier,
        filterableMetrics,
      };
    }

    case 'SET_SELECTED_TAB': {
      const { selectedTab } = action;
      return {
        ...state,
        selectedTab,
      };
    }

    case 'SET_SELECTED_QUESTION_AND_METRIC': {
      const { selectedQuestionAndMetric } = action;
      return {
        ...state,
        selectedTab: selectedQuestionAndMetric, // activate Tab
        selectedQuestionAndMetric,
      };
    }

    default: {
      return state;
    }
  }
};

export const setSelectedTab = metricConfiguration => dispatch =>
  dispatch({
    type: 'SET_SELECTED_TAB',
    selectedTab: metricConfiguration,
  });

export const setQuestionDropdownMetric =
  (metricName, surveyQuestion) => dispatch => {
    const metricDisplayName =
      metricName === 'Custom Percent' ? '% Selected answer' : metricName;
    const selectedQuestionAndMetric = {
      id: surveyQuestion.id,
      question_identifier: surveyQuestion.identifier,
      metric_name: metricName,
      translations: { en: { title: metricDisplayName } },
    };

    dispatch({
      type: 'SET_SELECTED_QUESTION_AND_METRIC',
      selectedQuestionAndMetric,
    });
  };

export const getAvailableFilters = includeSurveysFilter => dispatch =>
  server
    .get('/api/v2/company_trends/available_filters')
    .then(availableFilters => {
      if (!includeSurveysFilter) {
        delete availableFilters.surveys;
      }

      dispatch({
        type: 'INITIALIZE/AVAILABLE_FILTERS',
        availableFilters,
      });
    });

export const getAvailableAnswerFilters = questionIdentifier => dispatch =>
  server
    .get('/api/v2/company_trends/available_answers', {
      question_identifier: questionIdentifier,
      language: 'en',
    })
    .then(availableAnswers => {
      dispatch({
        type: 'INITIALIZE/AVAILABLE_FILTERS',
        availableFilters: {
          answers: {
            data: reduce(
              availableAnswers,
              (result, answer) => {
                result[answer.value] = answer.label;
                return result;
              },
              {}
            ),
          },
        },
      });
    });

export const getSurveyQuestionsByIdentifier = () => dispatch =>
  server
    .get('/api/v2/company_trends/available_questions')
    .then(({ filterableQuestionsByIdentifier, filterableMetrics }) => {
      dispatch({
        type: 'INITIALIZE/AVAILABLE_QUESTIONS',
        filterableQuestionsByIdentifier,
        filterableMetrics,
      });
    });

// selectors
export const getSelectedQuestion = state => {
  const { selectedTab, filterableQuestionsByIdentifier } =
    state.companyTrendsState;
  return values(flatten(filterableQuestionsByIdentifier)).find(
    q => q.identifier === selectedTab.question_identifier
  );
};

// utils
export const isSameConfiguration = (configurationA, configurationB) =>
  configurationA.question_identifier === configurationB.question_identifier &&
  configurationA.metric_name === configurationB.metric_name;
